import React, { createContext, useEffect, useMemo, useState } from 'react';
import { getManageKeys } from '../../../api/ManageKeys';
import { useStoreDetails } from '../StoreDetailsProvider/UseStoreDetails';

/**
 * CMS consolidation (Option B): content is served from System B
 * (`setting_content_blocks` + `setting_store_contents`) via the storeContent
 * map. `storeContent` is a flat key->value record, so we map the field type of
 * the content keys that are consumed through getKeyItem() (html / image) here.
 * Anything not listed is treated as plain text. Colors and time slots are read
 * directly as storeContent.* elsewhere and do not need entries here.
 */
const SYSTEM_B_FIELD_TYPES: Record<string, 'html' | 'image'> = {
  right_panel_text: 'html',
  right_panel_image_01: 'image',
  right_panel_image_02: 'image',
  terms_and_condition: 'html',
  by_opting_into_text_messages_you: 'html',
};

export interface ManageKeyItem {
  value: string;
  content_type: string;
  image_url: string | null;
}

interface ManageKeysContextProps {
  keysMap: Record<string, string>;
  keysItemMap: Record<string, ManageKeyItem>;
  getKey: (keyName: string, fallback?: string) => string;
  getKeyItem: (keyName: string) => ManageKeyItem | null;
  getFlag: (keyName: string, fallback?: boolean) => boolean;
  isLoading: boolean;
}

export const ManageKeysContext = createContext<ManageKeysContextProps>({
  keysMap: {},
  keysItemMap: {},
  getKey: (keyName: string, fallback?: string) => fallback || keyName,
  getKeyItem: () => null,
  getFlag: (keyName: string, fallback?: boolean) => fallback ?? false,
  isLoading: true,
});

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

export const ManageKeysProvider = ({ children }: { children: React.ReactNode }) => {
  const { storeDetails, storeContent } = useStoreDetails();
  // Legacy System A (manage_keys) maps. Empty for all current tenants; kept as
  // a transitional fallback until manage_keys is fully retired.
  const [legacyMap, setLegacyMap] = useState<Record<string, string>>({});
  const [legacyItemMap, setLegacyItemMap] = useState<Record<string, ManageKeyItem>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!storeDetails.brand_id) return;
    loadKeys(storeDetails.brand_id);
  }, [storeDetails.brand_id]);

  const loadKeys = (brand_id: number) => {
    getManageKeys(brand_id, (jsonRes: any, status: any) => {
      if (status === 200 && jsonRes?.data && Array.isArray(jsonRes.data) && jsonRes.data.length > 0) {
        const map: Record<string, string> = {};
        const itemMap: Record<string, ManageKeyItem> = {};
        jsonRes.data.forEach((item: any) => {
          if (item.key_name && (item.key_value || item.image_url)) {
            const rawValue = item.key_value ?? item.image_url;
            map[item.key_name] = item.content_type === 'html' ? stripHtml(rawValue) : rawValue;
            itemMap[item.key_name] = {
              value: item.key_value ?? '',
              content_type: item.content_type ?? 'text',
              image_url: item.image_url ?? null,
            };
          }
        });
        setLegacyMap(map);
        setLegacyItemMap(itemMap);
      } else {
        setLegacyMap({});
        setLegacyItemMap({});
      }
      setIsLoading(false);
    });
  };

  // System B (setting_content_blocks) is the source of truth. Build maps from
  // the flat storeContent record, deriving content_type/image_url per key.
  const { keysMap, keysItemMap } = useMemo(() => {
    const map: Record<string, string> = { ...legacyMap };
    const itemMap: Record<string, ManageKeyItem> = { ...legacyItemMap };

    Object.entries(storeContent || {}).forEach(([key, rawValue]) => {
      if (rawValue === undefined || rawValue === null || rawValue === '') return;
      const fieldType = SYSTEM_B_FIELD_TYPES[key];
      if (fieldType === 'image') {
        map[key] = String(rawValue);
        itemMap[key] = { value: String(rawValue), content_type: 'image', image_url: String(rawValue) };
      } else if (fieldType === 'html') {
        map[key] = stripHtml(String(rawValue));
        itemMap[key] = { value: String(rawValue), content_type: 'html', image_url: null };
      } else {
        map[key] = String(rawValue);
        itemMap[key] = { value: String(rawValue), content_type: 'text', image_url: null };
      }
    });

    return { keysMap: map, keysItemMap: itemMap };
  }, [storeContent, legacyMap, legacyItemMap]);

  const getKey = (keyName: string, fallback?: string): string => {
    const val = keysMap[keyName];
    return val !== undefined && val !== '' ? val : (fallback ?? keyName);
  };

  const getKeyItem = (keyName: string): ManageKeyItem | null => {
    return keysItemMap[keyName] ?? null;
  };

  /**
   * Boolean reader for `checkbox` content blocks. The admin stores '1'/'0',
   * but older rows (and hand-edited ones) may hold 'true'/'false'; anything
   * else — including an unset key — falls back to `fallback`.
   */
  const getFlag = (keyName: string, fallback = false): boolean => {
    const val = keysMap[keyName];
    if (val === undefined || val === '') return fallback;
    const normalized = String(val).trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
    return fallback;
  };

  const values: ManageKeysContextProps = {
    keysMap,
    keysItemMap,
    getKey,
    getKeyItem,
    getFlag,
    isLoading,
  };

  return (
    <ManageKeysContext.Provider value={values}>
      {children}
    </ManageKeysContext.Provider>
  );
};
